'use client';

import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Checkbox } from '@lichtblick/photon/molecules/checkbox';
import { Select } from '@lichtblick/photon/molecules/select';
import { Textarea } from '@lichtblick/photon/molecules/textarea';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import { useRouter } from 'next/navigation';
import { type FC, useActionState, useEffect, useState } from 'react';
import { submitLead } from '@/app/checkout-b2b/_actions/submit-lead';
import {
  LEAD_DATA_MAX_LENGTHS,
  MAX_INSTALLATION_YEAR,
  MAX_PEAK_OUTPUT,
  MIN_INSTALLATION_YEAR,
  MIN_PEAK_OUTPUT,
} from '@/constants';
import { CONSENTS } from '@/content';
import { i18n } from '@/i18n';
import { getFieldProps } from '@/lib/form-utils';
import { PATTERNS, toPatternIgnoringCase } from '@/patterns';
import { ContentBox, Fieldset, FormRow } from '../form/content';
import { Form } from '../form/form';
import { SubmitButton } from '../form/submit-button';
import { PV_PLANT_TYPES } from '../lead-form/lead-form.types';
import styles from './repowering-form.module.css';

export const RepoweringForm: FC = () => {
  const { push } = useRouter();
  const { trackCustomEvent } = useTracker();
  const [actionState, dispatch] = useActionState(submitLead, {});
  const { error, input, loading } = actionState;
  const [emailAddressInput, setEmailAddressInput] = useState(input?.emailAddress);
  const [messageInput, setMessageInput] = useState<string>();
  const formName = 'B2B Solar Repowering';

  useEffect(() => {
    if (actionState.trackingId) {
      trackCustomEvent({
        eventName: 'form_submit_B2B_solar_repowering',
        customProps: { unique_id: actionState.trackingId },
      });
      push('/danke-pv-repowering/');
    }
  }, [actionState, push, trackCustomEvent]);

  return (
    <Form className={styles['repowering-form']} dispatch={dispatch} trackingName={formName}>
      <ContentBox>
        <Headline renderAs="h1" size="m">
          Repowering
        </Headline>
        <Fieldset className={styles.fields} disabled={loading}>
          <Headline renderAs="h2" size="s">
            Firma / Betrieb
          </Headline>
          <Textfield
            {...getFieldProps(input, 'companyName', 'Betriebs- oder Firmenname')}
            maxLength={LEAD_DATA_MAX_LENGTHS.companyName}
          />
          <FormRow desktopColumns="3fr-1fr">
            <Textfield {...getFieldProps(input, 'street', 'Straße')} maxLength={LEAD_DATA_MAX_LENGTHS.street} />
            <Textfield
              {...getFieldProps(input, 'houseNumber', 'Hausnummer', { pattern: PATTERNS.houseNumber })}
              maxLength={LEAD_DATA_MAX_LENGTHS.houseNumber}
            />
          </FormRow>
          <FormRow desktopColumns="1fr-3fr">
            <Textfield
              {...getFieldProps(input, 'postalCode', 'PLZ', {
                pattern: PATTERNS.zipCode,
                message: i18n.validation.invalidPostalCode,
              })}
              maxLength={LEAD_DATA_MAX_LENGTHS.postalCode}
            />
            <Textfield
              {...getFieldProps(input, 'city', 'Ort', { pattern: PATTERNS.name })}
              maxLength={LEAD_DATA_MAX_LENGTHS.city}
            />
          </FormRow>
          <Headline renderAs="h2" size="s">
            Ansprechperson
          </Headline>
          <FormRow desktopColumns="1fr-1fr">
            <Textfield
              {...getFieldProps(input, 'firstname', 'Vorname', { pattern: PATTERNS.name })}
              maxLength={LEAD_DATA_MAX_LENGTHS.firstname}
            />
            <Textfield
              {...getFieldProps(input, 'lastname', 'Nachname', { pattern: PATTERNS.name })}
              maxLength={LEAD_DATA_MAX_LENGTHS.lastname}
            />
          </FormRow>
          <Textfield
            {...getFieldProps(input, 'telephoneNumber', 'Telefonnummer', {
              pattern: PATTERNS.phoneNumber,
              message: i18n.validation.invalidPhoneNumber,
            })}
            maxLength={LEAD_DATA_MAX_LENGTHS.telephoneNumber}
            type="tel"
          />
          <FormRow desktopColumns="1fr-1fr">
            <Textfield
              {...getFieldProps(input, 'emailAddress', 'E-Mail-Adresse', { message: i18n.validation.invalidEmail })}
              maxLength={LEAD_DATA_MAX_LENGTHS.emailAddress}
              onChange={({ target }) => setEmailAddressInput(target.value)}
              type="email"
            />
            <Textfield
              {...getFieldProps(
                input,
                'emailAddressRepeat',
                'E-Mail-Adresse wiederholen',
                emailAddressInput
                  ? {
                      pattern: `^${toPatternIgnoringCase(emailAddressInput)}$`,
                      message: i18n.validation.differingEmail,
                    }
                  : {
                      message: i18n.validation.invalidEmail,
                    },
              )}
              maxLength={LEAD_DATA_MAX_LENGTHS.emailAddress}
              type="email"
            />
          </FormRow>
          <Headline renderAs="h2" size="s">
            Anlage
          </Headline>
          <FormRow desktopColumns="1fr-1fr">
            <Select {...getFieldProps(input, 'plantType', 'Art der Anlage')}>
              {PV_PLANT_TYPES.map((plantType) => (
                <Select.Option key={plantType}>{plantType}</Select.Option>
              ))}
            </Select>
            <Textfield
              {...getFieldProps(input, 'peakOutput', 'Leistung der Solaranlage', {
                message: i18n.validation.invalidRange(MIN_PEAK_OUTPUT, MAX_PEAK_OUTPUT),
              })}
              max={MAX_PEAK_OUTPUT}
              min={MIN_PEAK_OUTPUT}
              rightSlot="kWp"
              type="number"
            />
            <Textfield
              {...getFieldProps(input, 'installationYear', 'Baujahr der Anlage', {
                message: i18n.validation.invalidRange(MIN_INSTALLATION_YEAR, MAX_INSTALLATION_YEAR, false),
              })}
              max={MAX_INSTALLATION_YEAR}
              min={MIN_INSTALLATION_YEAR}
              type="number"
            />
          </FormRow>
          <Headline renderAs="h2" size="s">
            Weitere Informationen (optional)
          </Headline>
          <Textarea
            {...getFieldProps(input, 'message', 'Ihre Nachricht (optional)', { optional: true })}
            footer={`${messageInput?.length ?? 0} / 2000 Zeichen`}
            maxLength={LEAD_DATA_MAX_LENGTHS.interest}
            onChange={({ target }) => setMessageInput(target.value)}
            rows={4}
          />
          <Checkbox
            data-testid="checkbox-marketingOptIn"
            errorMessage={i18n.validation.missing}
            label={<Checkbox.Label>{CONSENTS.marketing}</Checkbox.Label>}
          >
            <Checkbox.Checkbox name="marketingOptIn" value="true" />
          </Checkbox>
          <Checkbox
            data-testid="checkbox-privacyPolicyOptIn"
            errorMessage={i18n.validation.missing}
            label={<Checkbox.Label>{CONSENTS.privacyPolicy}</Checkbox.Label>}
          >
            <Checkbox.Checkbox name="privacyPolicyOptIn" required value="true" />
          </Checkbox>
          <input name="channelDetail" type="hidden" value="B2B PV" />
        </Fieldset>
        <SubmitButton error={error} label="Anfrage absenden" loading={loading} trackingName={formName} />
      </ContentBox>
    </Form>
  );
};
